// Native bridge adapter for the Capacitor app builds.
//
// WHY THERE ARE NO IMPORTS FROM '@capacitor/...' HERE
//
// The reason used to be "this app has no bundler". That was true of the 0.1
// chassis, which loaded js/app.js as a plain ES module with every specifier
// relative — and it is false here: this is Vite, and everything is under src/.
// A stale premise in the file anyone doing native work opens first, so it is
// corrected rather than deleted, because the conclusion still holds for a
// different and better reason.
//
// Capacitor's plugin packages resolve fine under Vite. What they do NOT do is
// work in a browser: each one is a thin wrapper that calls through to a native
// implementation which only exists inside the native runtime. Importing them
// here would bundle code into the WEB app that cannot run there, and turn a
// feature that should degrade into a hard failure at first use.
//
// The runtime injects a `window.Capacitor` global and proxies plugin calls
// through it, so that is what we use — one path that is present natively and
// absent on the web, which is exactly the shape the feature detection below
// wants. The npm packages still matter: they register the plugins on the NATIVE
// side via Gradle and SPM. They are just not part of the web module graph.
//
// EVERYTHING HERE IS FEATURE-DETECTED AND FAILS SOFT. Each entry point returns
// null/false when it did not handle the request, and every caller keeps its web
// path as the fallback. On the web `isNative` is false and nothing below runs.

import { PUBLIC_ORIGIN } from '../../app-config.js';

const cap = () => (typeof window !== 'undefined' ? window.Capacitor : null);

export const isNative = !!cap()?.isNativePlatform?.();
export const platform = cap()?.getPlatform?.() || 'web';

// Where the API lives. On the web that is the same origin the page came from,
// so '' (a root-absolute path) is right. In an app the page came from
// capacitor://localhost, which serves the bundle and knows nothing about
// /api/… — every request has to be sent to the real deployment.
export const API_BASE = isNative ? PUBLIC_ORIGIN : '';

/** The origin to BUILD shareable links from — a capacitor://localhost link is useless to a recipient. */
export function shareOrigin() {
  return isNative ? PUBLIC_ORIGIN : location.origin;
}

// The standalone legal/marketing pages are deliberately NOT bundled into the app
// payload (legal copy stays server-side so it can be corrected without an app
// release). That leaves root-absolute <a href="/privacy"> resolving to
// capacitor://localhost/privacy, which does not exist — a dead Privacy link,
// and App Review checks that one.
//
// Pointing them at the real site fixes it: capacitor.config.json declares no
// `allowNavigation` entry for that origin, so the WebView refuses to navigate
// there itself and hands the URL to the system browser — which is the intended
// UX anyway (leave the app, read the page, come back).
//
// Keep this set in sync with STANDALONE_PAGES in sw.js.
const STANDALONE_PAGES = ['/privacy', '/support', '/about'];

export function externalizeSiteLinks(root = document) {
  if (!isNative || !root?.querySelectorAll) return;
  for (const path of STANDALONE_PAGES) {
    for (const a of root.querySelectorAll(`a[href="${path}"]`)) {
      a.href = PUBLIC_ORIGIN + path;
      a.target = '_blank';
      a.rel = 'noopener noreferrer';
    }
  }
}

// Deep links. Inside an app the launch URL never appears in location.*; it
// arrives as an event, so boot-time routing has to be re-run against it.
export function onAppUrlOpen(handler) {
  if (!isNative) return;
  const App = plugin('App');
  if (!App?.addListener) return;
  try { App.addListener('appUrlOpen', (e) => { if (e?.url) handler(e.url); }); }
  catch { /* plugin unavailable — links just open in the browser */ }
}

/** Capacitor exposes plugins two ways depending on version; try both. */
export function plugin(name) {
  const c = cap();
  if (!c) return null;
  try { return c.Plugins?.[name] || c.registerPlugin?.(name) || null; }
  catch { return null; }
}

function blobToBase64(blob) {
  return new Promise((resolve, reject) => {
    const fr = new FileReader();
    fr.onload = () => resolve(String(fr.result).split(',')[1] || '');
    fr.onerror = () => reject(fr.error || new Error('read-failed'));
    fr.readAsDataURL(blob);
  });
}

/**
 * Share a file. Capacitor's Share plugin takes file URIs, not Blobs, so the
 * bytes are staged in the cache directory first.
 * Returns 'shared' | 'aborted', or null meaning "not handled — use the web path".
 */
export async function shareFile(blob, filename, title) {
  if (!isNative) return null;
  const Filesystem = plugin('Filesystem');
  const Share = plugin('Share');
  if (!Filesystem || !Share) return null;
  try {
    const data = await blobToBase64(blob);
    const { uri } = await Filesystem.writeFile({ path: filename, data, directory: 'CACHE' });
    await Share.share({ title, files: [uri] });
    return 'shared';
  } catch (e) {
    // Dismissing the sheet is a normal outcome, not a failure — it must NOT fall
    // through to the download path, which would silently save a file the user
    // just declined to share.
    if (isCancel(e)) return 'aborted';
    return null;
  }
}

export async function shareUrl(url, title, text) {
  if (!isNative) return null;
  const Share = plugin('Share');
  if (!Share) return null;
  try { await Share.share({ title, text, url }); return 'shared'; }
  catch (e) { return isCancel(e) ? 'aborted' : null; }
}

/** Native builds have no service-worker cache to name, so this is the version string. */
export async function appVersion() {
  if (!isNative) return null;
  const App = plugin('App');
  if (!App?.getInfo) return null;
  try {
    const { version, build } = await App.getInfo();
    return version ? `v${version} (${build})` : null;
  } catch { return null; }
}

function isCancel(e) {
  const m = String(e?.message || e || '').toLowerCase();
  return m.includes('cancel') || e?.name === 'AbortError';
}

// The Taptic Engine. `navigator.vibrate` does not exist on iOS at all, so the
// web path is a no-op on the one platform these apps are built for first.
//
// DO NOT CALL THIS DIRECTLY FROM A PAGE. `haptic()` in `core/util.js` is the
// one function an app calls; it delegates here when native and falls back to
// `navigator.vibrate` on the web, and it is the one that honours the user's
// haptics preference. For a long time both files carried a comment saying that
// bridge existed and neither file built it: nothing imported this function, so
// every shipped iOS app had no haptics at all, silently, and a phone with a
// chassis bug felt exactly like a phone with the preference switched off.
//
// THREE KINDS, not one. `impact` alone cannot express what a stepper, a
// segmented control or a completed payment actually call for:
//
//   impact       something moved or landed — a tap, a card settling
//   notification the OUTCOME of something — succeeded, warned, failed
//   selection    scrubbing through discrete values — a stepper, a segment
const IMPACT = { tick: 'LIGHT', pop: 'MEDIUM', heavy: 'HEAVY', undo: 'LIGHT' };
const NOTIFY = { success: 'SUCCESS', warning: 'WARNING', error: 'ERROR' };
const SELECT = new Set(['selection', 'selection-start', 'selection-end']);

export function nativeHaptic(kind = 'tick') {
  if (!isNative) return false;
  const Haptics = plugin('Haptics');
  if (!Haptics) return false;

  try {
    if (NOTIFY[kind]) {
      if (!Haptics.notification) return false;
      Haptics.notification({ type: NOTIFY[kind] });
      return true;
    }
    if (SELECT.has(kind)) {
      // Three calls, and a control that only ever ticks can use the middle one
      // on its own — `selectionChanged` outside a start/end pair is what the
      // plugin expects from a stepper.
      if (kind === 'selection-start') { Haptics.selectionStart?.(); return true; }
      if (kind === 'selection-end') { Haptics.selectionEnd?.(); return true; }
      if (!Haptics.selectionChanged) return false;
      Haptics.selectionChanged();
      return true;
    }
    if (!Haptics.impact) return false;
    Haptics.impact({ style: IMPACT[kind] || 'LIGHT' });
    return true;
  } catch { return false; }
}
